import React, { useState, useEffect } from 'react';
import { Users, Shield, CheckCircle2, XCircle, ChevronDown } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';

const ROLES = ['inspector', 'manager', 'reviewer', 'admin'];

const roleColor = (role) => {
  if (role === 'admin')     return { bg: '#ede9fe', color: '#5b21b6' };
  if (role === 'manager')   return { bg: '#dbeafe', color: '#1d4ed8' };
  if (role === 'reviewer')  return { bg: '#fef3c7', color: '#b45309' };
  return                           { bg: '#f0fdf4', color: '#166534' };
};

const UserManagement = () => {
  const { can } = useAuth();
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [saving, setSaving]   = useState(null); // userId being saved

  const fetchUsers = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('admin_user_list')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      setError('Failed to load users.');
      console.error(error);
    } else {
      setUsers(data || []);
    }
    setLoading(false);
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleRoleChange = async (userId, newRole) => {
    setSaving(userId);
    const { error } = await supabase.rpc('admin_set_user_role', {
      target_user_id: userId,
      new_role: newRole,
    });
    if (error) {
      alert('Failed to update role: ' + error.message);
    } else {
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
    }
    setSaving(null);
  };

  const handleToggleActive = async (userId, currentlyActive) => {
    setSaving(userId);
    const { error } = await supabase.rpc('admin_set_user_active', {
      target_user_id: userId,
      active: !currentlyActive,
    });
    if (error) {
      alert('Failed to update status: ' + error.message);
    } else {
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: !currentlyActive } : u));
    }
    setSaving(null);
  };

  if (!can.manageUsers) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
        <Shield style={{ width: 40, height: 40, margin: '0 auto 1rem', opacity: 0.4 }} />
        <p>You don't have permission to manage users.</p>
      </div>
    );
  }

  return (
    <div className="step-content fade-in">
      <div className="step-header">
        <Users className="w-8 h-8 text-blue-600" />
        <h2>User Management</h2>
        <p>Manage inspector accounts and roles</p>
      </div>

      {error && (
        <div style={{ padding: '0.875rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.5rem', color: '#dc2626', fontSize: '0.9rem' }}>
          {error}
        </div>
      )}

      <div style={{
        padding: '1rem', background: '#eff6ff', border: '1px solid #bfdbfe',
        borderRadius: '0.5rem', fontSize: '0.85rem', color: '#1d4ed8',
      }}>
        To add a new user, invite them via <strong>Supabase Dashboard → Authentication → Users → Invite user</strong>. They will appear here automatically once they accept.
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>Loading users...</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {users.map(user => {
            const rc = roleColor(user.role);
            const isSaving = saving === user.id;

            return (
              <div key={user.id} style={{
                padding: '1rem', background: user.is_active ? 'var(--surface-alt)' : '#f8fafc',
                border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                opacity: user.is_active ? 1 : 0.6,
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--text)', marginBottom: 2 }}>
                      {user.full_name || 'Unnamed user'}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                      {user.email}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {user.inspection_count || 0} inspection{user.inspection_count !== 1 ? 's' : ''}
                      {user.last_inspection_at && ` · Last: ${new Date(user.last_inspection_at).toLocaleDateString()}`}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                    {/* Role selector */}
                    <div style={{ position: 'relative' }}>
                      <select
                        value={user.role}
                        onChange={e => handleRoleChange(user.id, e.target.value)}
                        disabled={isSaving}
                        style={{
                          appearance: 'none', padding: '0.35rem 1.75rem 0.35rem 0.6rem',
                          borderRadius: '999px', border: 'none', fontWeight: 600,
                          fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'inherit',
                          background: rc.bg, color: rc.color,
                          opacity: isSaving ? 0.5 : 1,
                        }}
                      >
                        {ROLES.map(r => (
                          <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                        ))}
                      </select>
                      <ChevronDown style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 11, height: 11, color: rc.color, pointerEvents: 'none' }} />
                    </div>

                    {/* Active toggle */}
                    <button
                      onClick={() => handleToggleActive(user.id, user.is_active)}
                      disabled={isSaving}
                      title={user.is_active ? 'Deactivate user' : 'Activate user'}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        padding: 4, display: 'flex', alignItems: 'center',
                        opacity: isSaving ? 0.4 : 1,
                      }}
                    >
                      {user.is_active
                        ? <CheckCircle2 style={{ width: 20, height: 20, color: '#166534' }} />
                        : <XCircle style={{ width: 20, height: 20, color: '#dc2626' }} />
                      }
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UserManagement;
